import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  Plus,
  Edit,
  Trash2,
  Search,
  Loader2,
  FileText,
  Tags,
  ArrowLeft,
  User,
  Clock3,
} from "lucide-react";
import { toast } from "sonner";

import { blogService, categoryService } from "@/services/blogService";
import { cn } from "@/lib/utils";
import BlogForm from "./BlogForm";
import CategoryManager from "./CategoryManager";

const PAGE_SIZE = 9;

// Backend envelope { success, message, data } ya already-unwrapped, dono chalega
const pick = (r) => r?.data ?? r;

export default function AdminBlogs() {
  const [cards, setCards] = useState([]);
  const [pagination, setPagination] = useState({ page: 1, pages: 1, total: 0 });
  const [categories, setCategories] = useState([]);

  const [active, setActive] = useState("all"); // "all" ya category slug
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);

  const [loading, setLoading] = useState(true);
  const [editLoadingId, setEditLoadingId] = useState(null);

  const [formOpen, setFormOpen] = useState(false);
  const [selectedBlog, setSelectedBlog] = useState(null);
  const [catOpen, setCatOpen] = useState(false);

  // ---------------- LOAD ----------------

  const loadCategories = useCallback(async () => {
    try {
      const list = pick(await categoryService.getAll());
      const arr = Array.isArray(list) ? list : [];
      setCategories(arr);
      // selected category delete/rename ho gayi to "All" par wapas
      setActive((cur) =>
        cur !== "all" && !arr.some((c) => c.slug === cur) ? "all" : cur
      );
    } catch (error) {
      console.error("LOAD CATEGORIES ERROR:", error);
      toast.error(error?.response?.data?.message || "Failed to load categories");
    }
  }, []);

  const loadCards = useCallback(async () => {
    try {
      setLoading(true);
      const payload = pick(
        await blogService.getCards({
          page,
          limit: PAGE_SIZE,
          category: active === "all" ? undefined : active,
          search: search.trim() || undefined,
        })
      );
      setCards(payload?.blogs || []);
      setPagination(payload?.pagination || { page: 1, pages: 1, total: 0 });
    } catch (error) {
      console.error("LOAD BLOGS ERROR:", error);
      toast.error(error?.response?.data?.message || "Failed to load blogs");
    } finally {
      setLoading(false);
    }
  }, [page, active, search]);

  useEffect(() => {
    loadCategories();
  }, [loadCategories]);

  useEffect(() => {
    const t = setTimeout(loadCards, 300);
    return () => clearTimeout(t);
  }, [loadCards]);

  // ---------------- ACTIONS ----------------

  const handleAdd = () => {
    setSelectedBlog(null);
    setFormOpen(true);
  };

  // Card API me full data nahi hota, isliye edit se pehle slug se full blog laate hain
  const handleEdit = async (card) => {
    try {
      setEditLoadingId(card._id);
      const blog = pick(await blogService.getBySlug(card.slug));
      setSelectedBlog(blog);
      setFormOpen(true);
    } catch (error) {
      console.error("LOAD BLOG ERROR:", error);
      toast.error(error?.response?.data?.message || "Failed to load blog");
    } finally {
      setEditLoadingId(null);
    }
  };

  const handleDelete = async (card) => {
    const ok = window.confirm(
      `Delete "${card.title}"? Blog aur uski saari images Cloudinary se bhi delete ho jayengi.`
    );
    if (!ok) return;

    try {
      await blogService.delete(card._id);
      toast.success("Blog deleted successfully");

      if (cards.length === 1 && page > 1) setPage((p) => p - 1);
      else loadCards();
      loadCategories();
    } catch (error) {
      console.error("DELETE BLOG ERROR:", error);
      toast.error(error?.response?.data?.message || "Failed to delete blog");
    }
  };

  const handleFormClose = () => {
    setFormOpen(false);
    setSelectedBlog(null);
  };

  const handleSaved = () => {
    handleFormClose();
    loadCards();
    loadCategories();
  };

  const handleCategoriesChanged = () => {
    loadCategories();
    loadCards();
  };

  const totalBlogs = categories.reduce((sum, c) => sum + (c.blogCount || 0), 0);

  // ---------------- UI ----------------

  return (
    <div className="min-h-screen bg-slate-950 text-white" data-testid="admin-blogs-page">
      <div className="mx-auto max-w-7xl px-4 py-6 sm:px-6 lg:px-8">
        {/* HEADER */}
        <div className="mb-6 flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
          <div>
            <Link
              to="/admin/dashboard"
              className="mb-2 inline-flex items-center gap-1.5 text-xs font-semibold text-slate-400 hover:text-white"
            >
              <ArrowLeft className="h-3.5 w-3.5" />
              Back to Dashboard
            </Link>

            <div className="flex items-center gap-2">
              <FileText className="h-6 w-6 text-[#F5A623]" />
              <h1 className="text-2xl font-bold">Blog Management</h1>
            </div>

            <p className="mt-1 text-sm text-slate-400">
              Create, edit and delete blogs with full SEO settings.
            </p>
          </div>

          <div className="flex flex-wrap items-center gap-3">
            <button
              type="button"
              onClick={() => setCatOpen(true)}
              className="inline-flex flex-1 items-center justify-center gap-2 rounded-lg border border-slate-700 bg-slate-900 px-4 py-2.5 text-sm font-bold text-white transition hover:bg-slate-800 sm:flex-none"
              data-testid="admin-manage-categories-btn"
            >
              <Tags className="h-4 w-4 text-[#F5A623]" />
              Categories
            </button>

            <button
              type="button"
              onClick={handleAdd}
              className="inline-flex flex-1 items-center justify-center gap-2 rounded-lg bg-[#F5A623] px-4 py-2.5 text-sm font-bold text-slate-950 transition hover:bg-[#ffb638] sm:flex-none"
              data-testid="admin-new-blog-btn"
            >
              <Plus className="h-4 w-4" />
              Add Blog
            </button>
          </div>
        </div>

        {/* SEARCH */}
        <div className="relative mb-5 max-w-md">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-500" />
          <input
            type="text"
            placeholder="Search blog by title..."
            value={search}
            onChange={(e) => {
              setPage(1);
              setSearch(e.target.value);
            }}
            className="w-full rounded-lg border border-slate-700 bg-slate-900 py-2.5 pl-10 pr-4 text-sm text-white outline-none placeholder:text-slate-500 focus:border-[#F5A623]"
          />
        </div>

        {/* CATEGORY FILTER */}
        <div className="flex flex-wrap gap-2.5">
          <FilterChip
            label={`All${categories.length ? ` (${totalBlogs})` : ""}`}
            active={active === "all"}
            onClick={() => {
              setPage(1);
              setActive("all");
            }}
            testId="blog-filter-all"
          />
          {categories.map((c) => (
            <FilterChip
              key={c._id}
              label={`${c.name} (${c.blogCount || 0})`}
              active={active === c.slug}
              onClick={() => {
                setPage(1);
                setActive(c.slug);
              }}
              testId={`blog-filter-${c.slug}`}
            />
          ))}
        </div>

        {/* GRID */}
        {loading ? (
          <div className="flex items-center justify-center py-24 text-slate-400">
            <Loader2 className="mr-2 h-6 w-6 animate-spin" />
            Loading blogs...
          </div>
        ) : cards.length === 0 ? (
          <div className="mt-8 flex flex-col items-center justify-center rounded-xl border border-slate-800 bg-slate-900 px-4 py-20 text-center">
            <FileText className="mb-3 h-10 w-10 text-slate-600" />
            <h3 className="text-lg font-semibold text-slate-300">No blogs found</h3>
            <p className="mt-1 text-sm text-slate-500">
              {search || active !== "all"
                ? "Filter ya search badal ke dekho."
                : "Add Blog par click karke pehla blog banao."}
            </p>
          </div>
        ) : (
          <div className="mt-8 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {cards.map((p) => (
              <article
                key={p._id}
                className="group flex flex-col overflow-hidden rounded-xl border border-slate-200 bg-white transition-colors hover:border-[#F5A623] hover:shadow-md"
                data-testid={`admin-blog-card-${p._id}`}
              >
                <div className="relative h-44 overflow-hidden bg-slate-100">
                  {p.image ? (
                    <img
                      src={p.image}
                      alt={p.title}
                      className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
                    />
                  ) : null}
                  <div className="absolute inset-0 bg-gradient-to-t from-slate-950/70 to-transparent" />
                  <span className="absolute bottom-3 left-4 text-[11px] font-bold uppercase tracking-widest text-[#F5A623]">
                    {p.category?.name || "Uncategorized"}
                  </span>
                </div>

                <div className="flex flex-1 flex-col p-5 sm:p-6">
                  <h3 className="line-clamp-2 font-heading font-bold leading-snug text-slate-900 transition-colors group-hover:text-amber-700">
                    {p.title}
                  </h3>

                  <p className="mt-2 line-clamp-3 flex-1 text-sm leading-relaxed text-slate-600">
                    {p.shortDescription}
                  </p>

                <div className="mt-4 flex items-center gap-4 border-t border-slate-100 pt-4 text-xs text-slate-500">
  {/* Author */}
  <div className="flex min-w-0 items-center gap-1.5">
    <User className="h-3.5 w-3.5 shrink-0 text-slate-400" />

    <span className="truncate">
      {p.authorName || "Unknown Author"}
    </span>
  </div>

  {/* Read Time */}
  <div className="flex shrink-0 items-center gap-1.5">
    <Clock3 className="h-3.5 w-3.5 text-slate-400" />

    <span>
      {p.readTime ? `${p.readTime} min read` : "5 min read"}
    </span>
  </div>
</div>

                  <div className="mt-4 flex items-center gap-2 border-t border-slate-100 pt-4">
                    <button
                      type="button"
                      onClick={() => handleEdit(p)}
                      disabled={editLoadingId === p._id}
                      className="inline-flex flex-1 items-center justify-center gap-1.5 rounded-lg border border-slate-300 px-3 py-2 text-xs font-semibold text-slate-700 transition hover:border-[#F5A623] hover:text-amber-700 disabled:opacity-50"
                      data-testid={`admin-blog-edit-${p._id}`}
                    >
                      {editLoadingId === p._id ? (
                        <Loader2 className="h-3.5 w-3.5 animate-spin" />
                      ) : (
                        <Edit className="h-3.5 w-3.5" />
                      )}
                      Edit
                    </button>

                    <button
                      type="button"
                      onClick={() => handleDelete(p)}
                      className="inline-flex flex-1 items-center justify-center gap-1.5 rounded-lg border border-red-200 px-3 py-2 text-xs font-semibold text-red-600 transition hover:bg-red-50"
                      data-testid={`admin-blog-delete-${p._id}`}
                    >
                      <Trash2 className="h-3.5 w-3.5" />
                      Delete
                    </button>
                  </div>
                </div>
              </article>
            ))}
          </div>
        )}

        {/* PAGINATION */}
        {!loading && pagination.total > 0 && (
          <div className="mt-8 flex flex-col items-center justify-between gap-3 text-sm sm:flex-row">
            <span className="text-slate-400">
              Total: {pagination.total} · Page {pagination.page} of {pagination.pages}
            </span>
            <div className="flex gap-2">
              <button
                type="button"
                disabled={page <= 1}
                onClick={() => setPage((p) => p - 1)}
                className="rounded-md border border-slate-700 px-4 py-1.5 text-slate-200 disabled:opacity-40"
              >
                Prev
              </button>
              <button
                type="button"
                disabled={page >= pagination.pages}
                onClick={() => setPage((p) => p + 1)}
                className="rounded-md border border-slate-700 px-4 py-1.5 text-slate-200 disabled:opacity-40"
              >
                Next
              </button>
            </div>
          </div>
        )}
      </div>

      {formOpen && (
        <BlogForm
          blog={selectedBlog}
          categories={categories}
          onClose={handleFormClose}
          onSaved={handleSaved}
          onOpenCategories={() => setCatOpen(true)}
        />
      )}

      {catOpen && (
        <CategoryManager
          onClose={() => setCatOpen(false)}
          onChanged={handleCategoriesChanged}
        />
      )}
    </div>
  );
}

function FilterChip({ label, active, onClick, testId }) {
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={testId}
      className={cn(
        "rounded-full border px-4 py-1.5 text-sm font-medium transition-colors",
        active
          ? "border-[#F5A623] bg-[#F5A623] text-slate-950"
          : "border-slate-700 bg-slate-900 text-slate-300 hover:border-[#F5A623]"
      )}
    >
      {label}
    </button>
  );
}